<section class="tech" id="expertise" #root aria-labelledby="tech-title">
  <!-- ============================================================
       DECORATION — blobs, orbits, particles, streaks
       ============================================================ -->
  <div class="tech__deco" aria-hidden="true">
    <span class="tech__blob tech__blob--pink"></span>
    <span class="tech__blob tech__blob--violet"></span>
    <span class="tech__blob tech__blob--blue"></span>

    <!-- thin orbital lines, slowly rotating -->
    <svg class="tech__orbit tech__orbit--l" viewBox="0 0 400 400" fill="none">
      <ellipse cx="200" cy="200" rx="196" ry="150" stroke="url(#techOrbA)" stroke-width="1" />
      <circle class="tech__orbit-dot" cx="200" cy="50" r="7" fill="#a855f7" fill-opacity=".55" />
      <defs>
        <linearGradient id="techOrbA" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#ff4fa3" stop-opacity=".38" />
          <stop offset="100%" stop-color="#a855f7" stop-opacity="0" />
        </linearGradient>
      </defs>
    </svg>

    <svg class="tech__orbit tech__orbit--r" viewBox="0 0 400 400" fill="none">
      <ellipse cx="200" cy="200" rx="180" ry="140" stroke="url(#techOrbB)" stroke-width="1" />
      <circle class="tech__orbit-dot" cx="380" cy="200" r="6" fill="#5b7cff" fill-opacity=".5" />
      <circle class="tech__orbit-dot tech__orbit-dot--2" cx="20" cy="200" r="5" fill="#ff4fa3" fill-opacity=".5" />
      <defs>
        <linearGradient id="techOrbB" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#5b7cff" stop-opacity=".4" />
          <stop offset="100%" stop-color="#ff4fa3" stop-opacity="0" />
        </linearGradient>
      </defs>
    </svg>

    <!-- soft light streaks -->
    <svg class="tech__streaks" viewBox="0 0 1440 900" preserveAspectRatio="none" fill="none">
      <path class="tech__streak" d="M-40 700C260 640 520 760 820 660s420-180 700-90" stroke="url(#techStreak)" stroke-width="1.4" />
      <path class="tech__streak tech__streak--2" d="M-40 800C300 740 560 850 900 750s400-140 620-60" stroke="url(#techStreak)" stroke-width="1" />
      <defs>
        <linearGradient id="techStreak" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#ff4fa3" stop-opacity="0" />
          <stop offset="50%" stop-color="#a855f7" stop-opacity=".3" />
          <stop offset="100%" stop-color="#5b7cff" stop-opacity="0" />
        </linearGradient>
      </defs>
    </svg>

    <!-- glowing particles -->
    <span class="tech__motes">
      <i></i><i></i><i></i><i></i><i></i><i></i><i></i>
      <i></i><i></i><i></i><i></i><i></i><i></i><i></i>
    </span>

    <span class="tech__dotfield tech__dotfield--l"></span>
    <span class="tech__dotfield tech__dotfield--r"></span>
  </div>

  <div class="container tech__inner">
    <!-- ============================================================
         HEADER
         ============================================================ -->
    <header class="tech__head" #head>
      <span class="tech__badge">
        <svg class="tech__badge-star" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.9L12 2.5z" />
        </svg>
        OUR EXPERTISE
      </span>

      <h2 class="tech__title" id="tech-title">
        Technologies <span class="tech__title-grad">We Excel In</span>
      </h2>

      <span class="tech__divider" aria-hidden="true">
        <i></i><em></em><i></i>
      </span>

      <p class="tech__lede">
        We leverage modern technologies and frameworks to build scalable, secure
        and future-ready digital products.
      </p>
    </header>

    <!-- ============================================================
         TECHNOLOGY TILES
         ============================================================ -->
    <ul class="tech__grid" #grid>
      <li
        class="tech__card"
        *ngFor="let t of items; trackBy: trackTech"
        #card
        [attr.data-tone]="t.tone"
        tabindex="0"
      >
        <!-- inner wrapper: the float lives here so it never collides
             with the tilt applied to the card itself -->
        <span class="tech__lift">
          <span class="tech__sheen" aria-hidden="true"></span>

          <span class="tech__logo">
            <!-- no local asset for Flutter, so the mark is drawn inline -->
            <svg
              *ngIf="t.inline === 'flutter'"
              class="tech__logo-svg"
              viewBox="0 0 256 317"
              fill="none"
              aria-hidden="true"
            >
              <path fill="#47C5FB" d="M157 0 0 157l48 48L253 0z" />
              <path fill="#47C5FB" d="M156 145 72 229l48 49 48-48 85-85z" />
              <path fill="#00569E" d="m120 278 37 37h96l-85-85z" />
              <path fill="#00B5F8" d="m71 230 48-48 49 48-48 48z" />
            </svg>

            <img
              *ngIf="!t.inline"
              [src]="t.logo"
              [alt]="t.name + ' logo'"
              loading="lazy"
              decoding="async"
              width="52"
              height="52"
            />
          </span>

          <span class="tech__name">{{ t.name }}</span>

          <!-- bottom accent, pulsing left to right -->
          <span class="tech__accent" aria-hidden="true"></span>
        </span>
      </li>
    </ul>

    <!-- ============================================================
         CLOSING BANNER
         ============================================================ -->
    <div class="tech__banner">
      <span class="tech__banner-ico" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3 5 5.6v5.2c0 4 2.9 7.5 7 8.5 4.1-1 7-4.5 7-8.5V5.6z" />
          <path d="m12 8.4 1.1 2.4 2.4.8-2.4.8L12 15l-1.1-2.6-2.4-.8 2.4-.8z" />
        </svg>
      </span>

      <span class="tech__banner-copy">
        <b>
          Powering <span class="tech__banner-grad">digital innovation</span>
          with future-ready technologies.
        </b>
        <u>
          We combine the right tools, frameworks and platforms to deliver
          exceptional digital experiences.
        </u>
      </span>

      <span class="tech__banner-sheen" aria-hidden="true"></span>
    </div>
  </div>
</section>
